import type { OrgStructureEntity } from '@commenda/api';
import {
  Badge,
  CountryCode,
  FlagComponent,
  Icons,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@commenda/kakashi';
import {
  cn,
  getCountryName,
  TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT,
} from '@commenda/utils';
import { useStore } from '@xyflow/react';
import { ReactNode, useEffect, useRef, useState } from 'react';

const OPEN_DELAY = 120;
const CLOSE_DELAY = 100;

// Touch devices fire mouseenter on tap; gate hover logic on hover-capable input.
const canHover = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches;

export type EntityDetails = Pick<
  OrgStructureEntity,
  | 'legalName'
  | 'incorporationCountry'
  | 'keyPersons'
  | 'bankAccounts'
  | 'taxClassification'
  | 'isNonControlledEntity'
>;

interface EntityDetailsPopoverProps {
  entity: Readonly<EntityDetails>;
  typeLabel: string | null;
  // Shown, muted, while the entity has no legal name yet.
  fallbackName: string;
  children: ReactNode;
}

// Must render inside <ReactFlowProvider> — closes on zoom via useStore.
const EntityDetailsPopover = ({
  entity,
  typeLabel,
  fallbackName,
  children,
}: EntityDetailsPopoverProps) => {
  const [open, setOpen] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Subscribe so the close-on-zoom effect below still fires; we no longer
  // scale the popover with the canvas — informational content should stay
  // at native size regardless of how zoomed out the chart is.
  const zoom = useStore((s) => s.transform[2]);

  // Close on zoom. Radix Popper repositions on resize/scroll only — xyflow
  // pans+zooms via CSS transform, which fires neither, so the popover would
  // appear to drag along with the chart until a re-render snaps it back. The
  // user re-hovers after the gesture settles and it reopens correctly anchored.
  useEffect(() => {
    setOpen(false);
  }, [zoom]);

  const { keyPersons, bankAccounts, taxClassification } = entity;

  const hasDetailContent = keyPersons.count > 0 || bankAccounts.count > 0;
  const hasPopoverContent =
    hasDetailContent || !!taxClassification || !!typeLabel;

  useEffect(() => {
    return () => {
      if (openTimer.current) clearTimeout(openTimer.current);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const scheduleOpen = () => {
    if (!hasPopoverContent || !canHover()) return;
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    openTimer.current = setTimeout(() => setOpen(true), OPEN_DELAY);
  };

  const scheduleClose = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  const countryName = getCountryName(entity.incorporationCountry) ?? '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* PopoverAnchor (not Trigger): avoids Radix click-to-toggle fighting
          our hover-controlled open. nodrag+nopan stop ReactFlow intercepting. */}
      <PopoverAnchor asChild>
        <div
          className="nodrag nopan"
          // ReactFlow paints `cursor: pointer` on the .react-flow__node wrapper
          // for selectable nodes; externals are non-interactive so we override
          // here. Inline style wins over the parent's class-applied cursor.
          style={
            entity.isNonControlledEntity ? { cursor: 'default' } : undefined
          }
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
        >
          {children}
        </div>
      </PopoverAnchor>

      <PopoverContent
        className={cn(
          // Figma 711:3019 — uniform `gap-lg` between header / divider / details.
          'w-[320px] p-lg flex flex-col gap-lg origin-left',
          'data-[state=open]:[animation-duration:150ms]',
          'data-[state=open]:[animation-timing-function:cubic-bezier(0.215,0.61,0.355,1)]',
          'data-[state=closed]:[animation-duration:100ms]',
          'motion-reduce:animate-none motion-reduce:transition-none'
        )}
        side="right"
        sideOffset={8}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-lg">
          <span className="size-9 shrink-0">
            {entity.incorporationCountry && (
              <FlagComponent
                country={entity.incorporationCountry as CountryCode}
                countryName={countryName}
                _className="size-9"
              />
            )}
          </span>
          <div className="flex flex-col gap-xxs min-w-0">
            <span
              className={
                entity.legalName
                  ? 'txt-small-bold text-text-primary truncate'
                  : 'txt-small-medium text-text-disabled truncate'
              }
            >
              {entity.legalName || fallbackName}
            </span>
            <div className="flex items-center gap-sm flex-wrap">
              {typeLabel && (
                <Badge size="sm" variant="gray">
                  {typeLabel}
                </Badge>
              )}
              {taxClassification && (
                <>
                  {typeLabel && (
                    <span className="size-[4px] rounded-full bg-bg-tertiary shrink-0" />
                  )}
                  <Badge size="sm" variant="gray">
                    {
                      TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT[
                        taxClassification
                      ]
                    }
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>

        {hasDetailContent && (
          <>
            <div className="h-px bg-border-primary w-full" />
            <div className="flex flex-col gap-md">
              {bankAccounts.count > 0 && (
                <div className="flex gap-md items-start">
                  {/* Fixed-height column matches one chip row (line-height 15 + py-xxs*2)
                      so flex items-center pins the icon to the first row's center, even
                      when chips wrap to multiple lines. */}
                  <span className="shrink-0 text-fg-primary flex items-center h-[20px]">
                    <Icons.Bank className="size-4" />
                  </span>
                  <div className="flex flex-wrap gap-xxs items-center">
                    {bankAccounts.data.map((ba) => (
                      <span
                        key={ba.id}
                        className="font-semibold text-text-primary px-xs py-xxs rounded-full text-[12px] leading-[15px]"
                      >
                        {ba.name ?? ba.id}
                      </span>
                    ))}
                    {bankAccounts.count > bankAccounts.data.length && (
                      <span className="font-semibold text-text-tertiary px-xs py-xxs rounded-full text-[12px] leading-[15px]">
                        +{bankAccounts.count - bankAccounts.data.length} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {keyPersons.count > 0 && (
                <div className="flex gap-md items-start">
                  <span className="shrink-0 text-fg-primary flex items-center h-[20px]">
                    <Icons.Users01 className="size-4" />
                  </span>
                  <div className="flex flex-wrap gap-xxs items-center">
                    {keyPersons.data.map((kp) => (
                      <span
                        key={kp.directorId}
                        className="font-semibold text-text-primary px-xs py-xxs rounded-full text-[12px] leading-[15px]"
                      >
                        {kp.name}
                      </span>
                    ))}
                    {keyPersons.count > keyPersons.data.length && (
                      <span className="font-semibold text-text-tertiary px-xs py-xxs rounded-full text-[12px] leading-[15px]">
                        +{keyPersons.count - keyPersons.data.length} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export { EntityDetailsPopover };
